import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Footer from 'examples/Footer';
import DataTable from 'examples/Tables/DataTable';
import TopNavbar from 'examples/Navbars/TopNavbar';
import { useData } from '../../hooks';
import {
  apiGetZeroSalonItemList,
  apiUploadZeroSalonItemImage,
  apiCreateZeroSalonItem,
  apiUpdateZeroSalonItem,
  apiDeleteZeroSalonItem,
} from '../../api/zerosalonItem.api';

/** 제로살롱몰 상품 관리 페이지 - 앱에 노출될 제로살롱몰 상품 아이템을 CRUD 관리합니다. */
const ZeroSalonItemManagement = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formName, setFormName] = useState('');
  const [formLinkUrl, setFormLinkUrl] = useState('');
  // 빈 문자열을 유지하는 이유: 0 과 "가격 미입력"은 다르다. 0 으로 초기화하면 안 정한 상품이 0pt 로 저장된다.
  const [formPricePoint, setFormPricePoint] = useState('');
  const [formSortOrder, setFormSortOrder] = useState(0);
  const [formIsActive, setFormIsActive] = useState(true);
  const [formImageUrl, setFormImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);

  const { user, appIsReady } = useData();

  useEffect(() => {
    if (appIsReady && user) {
      fetchItems(page);
    }
  }, [page, user, appIsReady]);

  /** 제로살롱몰 상품 목록을 서버에서 불러옵니다. */
  const fetchItems = async (pageNum) => {
    setLoading(true);
    try {
      const response = await apiGetZeroSalonItemList({ page: pageNum, limit: 20 }, user.accessToken);
      if (response) {
        setItems(response.data || []);
        setTotal(response.total || 0);
      }
    } catch (error) {
      console.error('Failed to load zerosalon items:', error);
    } finally {
      setLoading(false);
    }
  };

  /** 상품 이미지를 S3에 업로드하고 URL을 폼 상태에 저장합니다. */
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const imageUrl = await apiUploadZeroSalonItemImage(file, user.accessToken);
      setFormImageUrl(imageUrl);
    } catch (error) {
      alert('이미지 업로드에 실패했습니다.');
    } finally {
      setUploading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingItem(null);
    setFormName('');
    setFormLinkUrl('');
    setFormPricePoint('');
    setFormSortOrder(0);
    setFormIsActive(true);
    setFormImageUrl('');
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setFormName(item.name || '');
    setFormLinkUrl(item.linkUrl || '');
    // null(미입력)은 빈 칸으로. `|| ''` 를 쓰면 0pt 상품까지 빈 칸이 된다.
    setFormPricePoint(item.pricePoint === null || item.pricePoint === undefined ? '' : String(item.pricePoint));
    setFormSortOrder(item.sortOrder || 0);
    setFormIsActive(item.isActive);
    setFormImageUrl(item.imageUrl || '');
    setDialogOpen(true);
  };

  /** 생성 또는 수정 요청을 서버에 전송합니다. editingItem 유무로 분기합니다. */
  const handleSave = async () => {
    if (!formImageUrl) {
      alert('이미지를 업로드해주세요.');
      return;
    }
    if (!formName.trim()) {
      alert('상품명을 입력해주세요.');
      return;
    }

    // 이 값은 앱에서 그대로 열린다. 잘못 저장되면 사용자가 죽은 링크를 누르게 되므로
    // 서버 검증(400)에 앞서 여기서 먼저 걸러 준다. 비우는 것은 허용 — 몰 첫 화면으로 간다.
    const linkUrl = formLinkUrl.trim();
    if (linkUrl && !/^https?:\/\//i.test(linkUrl)) {
      alert('상품 URL은 http:// 또는 https:// 로 시작해야 합니다.');
      return;
    }

    // 이 값은 앱 상품 카드에 그대로 찍힌다. 잘못 저장되면 회원이 틀린 가격을 보고 몰로 넘어간다.
    // 빈 칸은 "가격 미표시"를 뜻하므로 null 로 보낸다 — 0pt 와 구분해야 한다.
    const rawPrice = String(formPricePoint).trim();
    let pricePoint = null;
    if (rawPrice !== '') {
      const parsed = Number(rawPrice);
      if (!Number.isInteger(parsed) || parsed < 0) {
        alert('가격(pt)은 0 이상의 정수로 입력해주세요.');
        return;
      }
      pricePoint = parsed;
    }

    try {
      const body = {
        imageUrl: formImageUrl,
        name: formName.trim(),
        linkUrl,
        pricePoint,
        sortOrder: formSortOrder,
        isActive: formIsActive,
      };

      if (editingItem) {
        await apiUpdateZeroSalonItem(editingItem.id, body, user.accessToken);
      } else {
        await apiCreateZeroSalonItem(body, user.accessToken);
      }

      setDialogOpen(false);
      fetchItems(page);
    } catch (error) {
      // 서버가 이유를 돌려주면 그대로 보여준다 (예: URL 형식 오류 400)
      const reason = error?.response?.data?.reason;
      alert(reason ? `저장에 실패했습니다.\n${reason}` : '저장에 실패했습니다.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('상품을 삭제하시겠습니까?')) return;

    try {
      await apiDeleteZeroSalonItem(id, user.accessToken);
      fetchItems(page);
    } catch (error) {
      alert('삭제에 실패했습니다.');
    }
  };

  /** 활성 상태 토글 - isActive 값만 PATCH로 전송합니다. */
  const handleToggleActive = async (item) => {
    try {
      await apiUpdateZeroSalonItem(item.id, { isActive: !item.isActive }, user.accessToken);
      fetchItems(page);
    } catch (error) {
      alert('상태 변경에 실패했습니다.');
    }
  };

  const columns = [
    { Header: 'ID', accessor: 'id', align: 'left' },
    { Header: '미리보기', accessor: 'preview', align: 'center' },
    { Header: '상품명', accessor: 'name', align: 'left' },
    { Header: '가격(pt)', accessor: 'pricePoint', align: 'right' },
    { Header: '이동 URL', accessor: 'linkUrl', align: 'left' },
    { Header: '정렬순서', accessor: 'sortOrder', align: 'center' },
    { Header: '활성', accessor: 'isActive', align: 'center' },
    { Header: '관리', accessor: 'actions', align: 'center' },
  ];

  const rows = items.map((item) => ({
    id: item.id,
    preview: (
      <img
        src={item.imageUrl}
        alt={item.name}
        style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8 }}
      />
    ),
    name: item.name,
    // 가격 미입력(null)은 앱에서도 감춰지므로 표에서도 "미입력"으로 구분해 보여준다.
    pricePoint:
      item.pricePoint === null || item.pricePoint === undefined ? (
        <MDTypography variant="caption" color="text">
          미입력
        </MDTypography>
      ) : (
        <MDTypography variant="button" fontWeight="medium">
          {Number(item.pricePoint).toLocaleString()}pt
        </MDTypography>
      ),
    // 상품마다 다른 URL. 비어 있으면 앱이 몰 첫 화면으로 보낸다.
    linkUrl: item.linkUrl ? (
      <a
        href={item.linkUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: '#3AA9A0', wordBreak: 'break-all' }}
        title={item.linkUrl}
      >
        {item.linkUrl.length > 40 ? `${item.linkUrl.slice(0, 40)}…` : item.linkUrl}
      </a>
    ) : (
      <MDTypography variant="caption" color="text">
        미지정 — 몰 첫 화면
      </MDTypography>
    ),
    sortOrder: item.sortOrder,
    isActive: (
      <Switch
        checked={item.isActive}
        onChange={() => handleToggleActive(item)}
        color="primary"
      />
    ),
    actions: (
      <MDBox display="flex" gap={1}>
        <IconButton size="small" onClick={() => openEditDialog(item)}>
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" color="error" onClick={() => handleDelete(item.id)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </MDBox>
    ),
  }));

  return (
    <DashboardLayout>
      <TopNavbar />

      <MDBox display="flex" mr={2} justifyContent="right">
        <MDButton variant="gradient" color="info" onClick={openCreateDialog}>
          상품 추가
        </MDButton>
      </MDBox>

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  제로살롱몰 상품 관리
                </MDTypography>
              </MDBox>

              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={true}
                  showTotalEntries={true}
                  noEndBorder
                  loading={loading}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? '상품 수정' : '상품 추가'}</DialogTitle>
        <DialogContent>
          <MDBox display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="상품명"
              fullWidth
              required
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="예: 에코후레쉬 프리미엄 곰팡이제거제"
            />

            <TextField
              label="상품 URL"
              fullWidth
              value={formLinkUrl}
              onChange={(e) => setFormLinkUrl(e.target.value)}
              placeholder="https://zerosalonmall.com/product/detail.html?product_no=12"
              helperText="앱에서 이 상품을 누르면 이동할 주소입니다. 비워 두면 몰 첫 화면으로 갑니다."
            />

            <TextField
              label="가격(pt)"
              type="number"
              fullWidth
              value={formPricePoint}
              onChange={(e) => setFormPricePoint(e.target.value)}
              placeholder="예: 12000"
              inputProps={{ min: 0, step: 1 }}
              helperText="앱 상품 카드에 표시됩니다. 1원 = 1pt. 비워 두면 가격을 감춥니다."
            />

            <TextField
              label="정렬순서"
              type="number"
              fullWidth
              value={formSortOrder}
              onChange={(e) => setFormSortOrder(Number(e.target.value))}
            />

            <MDBox display="flex" alignItems="center" gap={1}>
              <MDTypography variant="body2">활성 상태</MDTypography>
              <Switch
                checked={formIsActive}
                onChange={(e) => setFormIsActive(e.target.checked)}
              />
            </MDBox>

            <MDBox>
              <MDTypography variant="body2" mb={1}>
                상품 이미지 <span style={{ color: '#d32f2f' }}>*</span>
              </MDTypography>
              <input type="file" accept="image/*" onChange={handleFileUpload} disabled={uploading} />
              {uploading && (
                <MDTypography variant="caption" color="info">
                  업로드 중...
                </MDTypography>
              )}
            </MDBox>

            {formImageUrl && (
              <MDBox>
                <MDTypography variant="caption" color="text">
                  미리보기:
                </MDTypography>
                <img
                  src={formImageUrl}
                  alt="미리보기"
                  style={{ width: '100%', maxHeight: 200, objectFit: 'contain', marginTop: 8, borderRadius: 8 }}
                />
              </MDBox>
            )}

            {/* 실제로 어디로 가는지 저장 전에 눈으로 확인한다 */}
            <MDBox p={1.5} sx={{ backgroundColor: '#f0faf9', borderRadius: 1, border: '1px solid #3AA9A0' }}>
              <MDTypography variant="caption" color="text">
                클릭 시 이동 URL:{' '}
                <strong style={{ wordBreak: 'break-all' }}>
                  {formLinkUrl.trim() || 'https://zerosalonmall.com/ (URL 미입력 시 몰 첫 화면)'}
                </strong>
              </MDTypography>
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>취소</Button>
          <Button onClick={handleSave} variant="contained" color="primary" disabled={uploading}>
            저장
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
};

export default ZeroSalonItemManagement;
